/**
 * Tesseract OSD (orientation & script detection) adapter — the port of
 * pytesseract.image_to_osd(--psm 0).
 *
 * OSD needs the LEGACY engine + osd.traineddata, so we spin up a dedicated
 * worker with OEM.TESSERACT_ONLY and lang 'osd'. tesseract.js exposes the result
 * as `orientation_degrees` / `orientation_confidence`, which correspond to the
 * "Orientation in degrees" / "Orientation confidence" lines pytesseract returns
 * as `rotate` / `orientation_conf`. We gate on confidence ≥ 1.0 (OSD_MIN_CONF)
 * in the orient stage; here we just surface the raw numbers.
 *
 * tesseract.js is a runtime dependency (not Angular); the worker is created
 * lazily and reused.
 */
import { createWorker, OEM, type Worker } from 'tesseract.js';
import { ImagePixels } from '../../core/ports';
import { OsdDetector, OsdResult } from '../ports';
import { pixelsToCanvas } from './canvas-image';

export interface TesseractOsdOptions {
  /** Path that hosts osd.traineddata (default: site root ''). */
  langPath?: string;
  /**
   * Pin a specific tesseract-core `.wasm.js` build instead of letting
   * tesseract.js auto-select one. The auto-selected relaxed-SIMD core fails in
   * some Firefox releases; callers pass the stable SIMD core here.
   */
  corePath?: string;
  /** Reuse a pre-created OSD worker instead of creating one. */
  worker?: Worker;
}

export class TesseractOsd implements OsdDetector {
  private worker?: Worker;
  private initPromise?: Promise<Worker>;
  private readonly langPath?: string;
  private readonly corePath?: string;

  constructor(opts: TesseractOsdOptions = {}) {
    this.worker = opts.worker;
    this.langPath = opts.langPath;
    this.corePath = opts.corePath;
  }

  private ready(): Promise<Worker> {
    if (this.worker) return Promise.resolve(this.worker);
    this.initPromise ??= createWorker('osd', OEM.TESSERACT_ONLY, {
      ...(this.langPath ? { langPath: this.langPath } : {}),
      ...(this.corePath ? { corePath: this.corePath } : {}),
      legacyCore: true,
      legacyLang: true,
    }).then((w) => (this.worker = w));
    return this.initPromise;
  }

  async detect(img: ImagePixels): Promise<OsdResult | null> {
    const worker = await this.ready();
    // Pass a <canvas>, not raw ImageData: leptonica sniffs the input by file
    // signature and rejects raw RGBA ("Unknown format / cannot read image") in
    // some browsers (notably Firefox). A canvas is decoded reliably everywhere.
    const canvas = pixelsToCanvas(img);
    const res = await worker.detect(canvas as unknown as HTMLCanvasElement);
    const deg = res.data.orientation_degrees;
    const conf = res.data.orientation_confidence;
    if (deg == null || conf == null) return null;
    return { rotate: ((deg % 360) + 360) % 360, orientationConf: conf };
  }

  async terminate(): Promise<void> {
    await this.worker?.terminate();
  }
}
